<template>
  <q-page id="vendas-online">
    <div class="q-pa-md">
      <sem-permissao v-if="!hasVendaOnlineListarPermissao" />

      <template v-else>
        <q-banner v-if="pendencias.length" class="bg-orange-1 text-orange-10 q-mb-md" rounded data-test="banner-pendencias">
          <template #avatar>
            <q-icon name="visibility_off" color="orange" />
          </template>
          O Gerar link está desligado até resolver:
          <q-list dense class="q-mt-xs">
            <q-item v-for="pendencia in pendenciasDoAviso" :key="pendencia.item" class="q-px-none" data-test="pendencia">
              <q-item-section>{{ pendencia.mensagem }}</q-item-section>
              <q-item-section side>
                <q-btn
                  v-if="pendencia.acao.atalho"
                  flat
                  dense
                  no-caps
                  color="primary"
                  :label="pendencia.acao.rotulo"
                  data-test="pendencia-atalho"
                  @click="openAtalho(pendencia.atalho)"
                />
                <span v-else class="text-caption" data-test="pendencia-com-dourasoft">{{ pendencia.acao.rotulo }}</span>
              </q-item-section>
            </q-item>
          </q-list>
        </q-banner>

        <q-banner v-if="avisos.length" dense class="bg-blue-1 text-blue-10 q-mb-md" rounded data-test="banner-avisos">
          <template #avatar>
            <q-icon name="info" color="primary" />
          </template>
          <q-list dense>
            <q-item v-for="aviso in avisos" :key="aviso.item" class="q-px-none" data-test="aviso">
              <q-item-section>{{ aviso.mensagem }}</q-item-section>
              <q-item-section side>
                <q-btn flat dense no-caps color="primary" label="Abrir Templates" data-test="aviso-atalho" @click="openAtalho(aviso.atalho)" />
              </q-item-section>
            </q-item>
          </q-list>
        </q-banner>

        <q-banner v-if="erro" dense class="bg-red-1 text-negative q-mb-md" data-test="painel-erro">{{ erro }}</q-banner>

        <vendas-online-indicadores :indicadores="vendasOnlineIndicadores" class="q-mb-md" :class="{ 'q-pr-xl': isVendaOnlineConfiguravel }" />

        <div v-if="selecionadas.length" class="row items-center q-gutter-sm q-mb-md">
          <q-btn
            unelevated
            no-caps
            icon="block"
            color="negative"
            :label="`Cancelar ${formatTotal(selecionadas.length, 'selecionada', 'selecionadas')}`"
            data-test="cancelar-selecionadas"
            @click="openCancelamentoMassa"
          />
          <q-btn flat no-caps label="Limpar seleção" color="primary" data-test="limpar-selecao" @click="selecionadas = []" />
        </div>

        <vendas-online-lista
          v-model:status="status"
          v-model:periodo="periodo"
          v-model:operadora-id="operadoraId"
          v-model:selecionadas="selecionadas"
          :vendas="vendasOnlineLista.vendas"
          :avisos="vendasOnlineLista.avisos"
          :paging="vendasOnlineLista.paging"
          :carregando="isVendasOnlineListaLoading"
          :operadoras="operadoras"
          :pode-criar="hasVendaOnlineCriarPermissao"
          :pode-resolver="hasVendaOnlineEditarPermissao"
          @abrir="open"
          @pedir="requestPagina"
          @atualizar="reload"
          @copiar="copyLinkDaLinha"
          @reenviar="resendDaLinha"
          @cancelar="cancelDaLinha"
          @resolver="(venda) => openResolucao({ venda })"
          @resolver-aviso="(aviso) => openResolucao({ aviso })"
        />

        <vendas-online-previsao class="q-mt-lg" />
      </template>
    </div>

    <q-dialog v-model="detalheAberto">
      <q-card style="min-width: 380px; max-width: 640px" data-test="detalhe-venda-online">
        <q-card-section class="row items-center no-wrap q-pb-none">
          <div class="text-h6">{{ detalhe ? `${detalhe.codigo} · ${detalhe.cliente_nome}` : "Venda Online" }}</div>
          <q-space />
          <q-btn icon="close" flat round dense data-test="fechar-detalhe" @click="detalheAberto = false" />
        </q-card-section>
        <template v-if="detalhe">
          <q-card-section>
            <q-chip
              dense
              square
              text-color="white"
              :color="getVendaOnlineStatus(detalhe.status).cor"
              :icon="getVendaOnlineStatus(detalhe.status).icone"
              data-test="detalhe-status"
            >
              {{ getVendaOnlineStatus(detalhe.status).rotulo }}
            </q-chip>
            <contagem-cancelamento v-if="detalhe.status === 'cancelando'" :prazo="detalhe.cancelamento_prazo_em" />
            <div class="text-caption text-grey-7">Operador: {{ detalhe.operadora_nome }} · vale até {{ formatMomento(detalhe.corte_em) }}</div>
            <q-banner v-if="detalhe.conflito_pendente" dense class="bg-red-1 text-red-10 q-mt-sm" data-test="detalhe-conflito">
              Conflito: {{ (detalhe.conflito_motivos || []).map(formatConflitoMotivo).join(", ") }}
            </q-banner>
            <q-banner v-if="detalhe.aguardando_causa" dense class="bg-orange-1 text-orange-10 q-mt-sm" data-test="detalhe-aguardando">
              Aguardando a causa: {{ (detalhe.conflito_aguardando || []).map(formatConflitoMotivo).join(", ") }}. O aviso sai da fila sozinho quando ela acabar.
            </q-banner>
            <q-banner v-if="!detalhe.conflito_pendente && detalhe.conflito_resolvido_em" dense class="bg-grey-2 q-mt-sm" data-test="detalhe-conflito-resolvido">
              Conflito resolvido em {{ formatMomento(detalhe.conflito_resolvido_em) }}: {{ detalhe.conflito_resolvido_nota }}
            </q-banner>
          </q-card-section>
          <q-card-section class="q-pt-none">
            <div v-for="item in detalhe.itens" :key="item.id" class="row" data-test="detalhe-item">
              <div class="col">{{ item.quantidade }}× {{ item.produto_nome }}</div>
              <div class="col-auto">R$ {{ $currency(item.valor_total) }}</div>
            </div>
            <div v-if="detalhe.valor_taxa_conveniencia > 0" class="row" data-test="detalhe-taxa">
              <div class="col">Taxa de conveniência</div>
              <div class="col-auto">R$ {{ $currency(detalhe.valor_taxa_conveniencia) }}</div>
            </div>
            <div class="row text-weight-bold q-mt-xs">
              <div class="col">Total</div>
              <div class="col-auto">R$ {{ $currency(detalhe.valor_total) }}</div>
            </div>
          </q-card-section>
          <q-card-section v-if="detalhe.status === 'ativa' && detalhe.link_url" class="q-pt-none" data-test="detalhe-link">
            <div class="row q-gutter-sm">
              <q-btn
                outline
                dense
                no-caps
                icon="content_copy"
                label="Copiar link"
                color="primary"
                data-test="copiar-link"
                @click="copyText(detalhe.link_url, 'Link copiado')"
              />
              <q-btn
                v-if="detalhe.copia_cola"
                outline
                dense
                no-caps
                icon="content_copy"
                label="Copiar Pix copia e cola"
                color="primary"
                data-test="copiar-pix"
                @click="copyText(detalhe.copia_cola, 'Pix copia e cola copiado')"
              />
              <q-btn
                v-if="detalhe.linha_digitavel"
                outline
                dense
                no-caps
                icon="content_copy"
                label="Copiar linha digitável"
                color="primary"
                data-test="copiar-linha-digitavel"
                @click="copyText(detalhe.linha_digitavel, 'Linha digitável copiada')"
              />
              <q-btn
                v-if="detalhe.boleto_url"
                outline
                dense
                no-caps
                icon="picture_as_pdf"
                label="Abrir boleto (PDF)"
                color="primary"
                type="a"
                target="_blank"
                rel="noopener noreferrer"
                :href="detalhe.boleto_url"
                data-test="abrir-boleto"
              />
              <q-btn
                v-if="whatsappUrl"
                outline
                dense
                no-caps
                icon="chat"
                label="Enviar pelo WhatsApp"
                color="green-8"
                type="a"
                target="_blank"
                :href="whatsappUrl"
                data-test="enviar-whatsapp"
              />
            </div>
            <div v-if="detalhe.linha_digitavel" class="q-mt-sm">
              <div class="text-caption text-grey-7">Linha digitável do boleto</div>
              <div class="text-body2" style="word-break: break-all" data-test="linha-digitavel">{{ detalhe.linha_digitavel }}</div>
            </div>
          </q-card-section>
          <q-card-section v-if="detalhe.valor_pago !== null && detalhe.valor_pago !== undefined" class="q-pt-none" data-test="detalhe-liquidacao">
            <div class="row"><div class="col">Pago</div><div class="col-auto">R$ {{ $currency(detalhe.valor_pago) }}</div></div>
            <div class="row"><div class="col">Taxa de cobrança</div><div class="col-auto">R$ {{ $currency(detalhe.valor_taxa_gateway) }}</div></div>
            <div class="row"><div class="col">Líquido</div><div class="col-auto">R$ {{ $currency(detalhe.valor_liquido) }}</div></div>
            <div class="row"><div class="col">Crédito na conta</div><div class="col-auto">{{ formatMomento(detalhe.data_repasse) }}</div></div>
            <div v-if="detalhe.valores_estimados" class="text-caption text-deep-orange-8" data-test="valores-estimados">
              Estimados: o pagamento online não informou taxa, líquido ou data do crédito
            </div>
          </q-card-section>
          <q-card-section v-if="detalhe.lancamentos && detalhe.lancamentos.length" class="q-pt-none">
            <div class="text-subtitle2">Financeiro</div>
            <div v-for="lancamento in detalhe.lancamentos" :key="lancamento.id" class="row" data-test="detalhe-lancamento">
              <div class="col">{{ formatPapel(lancamento.papel) }} · {{ formatData(lancamento.liquidacao) }}</div>
              <div class="col-auto">R$ {{ $currency(lancamento.valor) }}</div>
            </div>
          </q-card-section>
          <q-card-section v-if="ofertaNovaVenda" class="q-pt-none" data-test="oferta-nova-venda">
            <q-banner dense class="bg-blue-1 text-blue-10">
              <template v-if="ofertaNovaVenda === copiaGuardada">
                A cópia desta venda cancelada ainda não entrou toda no caixa. Confira o caixa e tente de novo.
              </template>
              <template v-else>
                Venda cancelada. Quer abrir uma nova venda com o mesmo cliente e itens? Você pode editar antes de gerar o link.
              </template>
              <template #action>
                <q-btn flat no-caps icon="add_shopping_cart" label="Fazer nova venda" color="primary" data-test="nova-venda" @click="startNovaVenda" />
              </template>
            </q-banner>
          </q-card-section>
          <q-card-section class="q-pt-none">
            <div class="text-subtitle2">Linha do tempo</div>
            <div v-if="detalhe.eventos_total > detalhe.eventos.length" class="text-caption text-grey-7" data-test="eventos-recortados">
              Mostrando os {{ detalhe.eventos.length }} eventos mais recentes de {{ detalhe.eventos_total }}.
            </div>
            <q-timeline dense color="primary">
              <q-timeline-entry
                v-for="evento in detalhe.eventos"
                :key="evento.id"
                :title="formatEventoRotulo(evento)"
                :subtitle="`${formatMomento(evento.created)} · ${evento.user_nome || formatEventoOrigem(evento.origem)}`"
                data-test="detalhe-evento"
              />
            </q-timeline>
          </q-card-section>
        </template>
        <q-card-actions v-if="isReenviavel || isCancelavelNoDetalhe" align="right">
          <q-btn
            v-if="isReenviavel"
            outline
            no-caps
            icon="send"
            label="Reenviar link"
            color="primary"
            :loading="reenviando"
            data-test="reenviar-link"
            @click="resendLink"
          />
          <q-btn
            v-if="isCancelavelNoDetalhe"
            outline
            no-caps
            icon="block"
            label="Cancelar venda"
            color="negative"
            :loading="cancelando"
            data-test="cancelar-venda"
            @click="openCancelamento"
          />
        </q-card-actions>
        <q-dialog v-model="cancelamentoAberto" persistent>
          <q-card style="min-width: 360px" data-test="dialogo-cancelar">
            <q-card-section class="row items-center no-wrap">
              <div class="text-h6">Cancelar venda {{ detalhe && detalhe.codigo }}</div>
              <q-space />
              <q-btn icon="close" flat round dense :disable="cancelando" data-test="fechar-cancelamento" @click="cancelamentoAberto = false" />
            </q-card-section>
            <q-card-section class="q-pt-none">
              <div class="q-mb-sm">O cancelamento não pode ser desfeito. Se o Pix já tiver sido pago, a venda fica paga e não é cancelada.</div>
              <q-banner v-if="detalhe && detalhe.status === 'ativa'" dense class="bg-orange-1 text-orange-10 q-mb-sm" data-test="aviso-pix-pagavel">
                A cobrança é cancelada agora. A venda fica em Cancelando por até 3 minutos, até o cancelamento ser confirmado; só então a reserva volta ao estoque. Se o cliente pagar nesse meio-tempo, a venda fica paga.
              </q-banner>
              <q-input
                v-model="motivoCancelamento"
                type="textarea"
                autogrow
                outlined
                dense
                label="Motivo"
                :maxlength="MOTIVO_MAX"
                data-test="motivo-cancelamento"
              />
            </q-card-section>
            <q-card-actions align="right">
              <q-btn
                unelevated
                no-caps
                label="Cancelar venda"
                color="negative"
                :loading="cancelando"
                :disable="!motivoCancelamento.trim()"
                data-test="confirmar-cancelamento"
                @click="cancelVenda"
              />
            </q-card-actions>
          </q-card>
        </q-dialog>
      </q-card>
    </q-dialog>

    <q-dialog v-model="massaAberta" persistent>
      <q-card style="min-width: 380px; max-width: 640px" data-test="dialogo-cancelar-massa">
        <q-card-section class="row items-center no-wrap">
          <div class="text-h6">Cancelar vendas selecionadas</div>
          <q-space />
          <q-btn icon="close" flat round dense :disable="cancelandoMassa" data-test="fechar-massa" @click="massaAberta = false" />
        </q-card-section>
        <template v-if="resultadosMassa">
          <q-card-section class="q-pt-none">
            <div class="text-weight-bold q-mb-sm" data-test="resumo-massa">{{ resumoMassa }}</div>
            <q-list dense bordered separator>
              <q-item v-for="resultado in resultadosMassa" :key="resultado.id" data-test="resultado-massa">
                <q-item-section>
                  <q-item-label>{{ resultado.codigo || codigosSelecionados[resultado.id] || "Venda não encontrada" }}</q-item-label>
                  <q-item-label caption>{{ resultado.mensagem }}</q-item-label>
                </q-item-section>
                <q-item-section side>
                  <q-chip dense square text-color="white" :color="RESULTADOS_MASSA[resultado.resultado].cor">
                    {{ RESULTADOS_MASSA[resultado.resultado].rotulo }}
                  </q-chip>
                </q-item-section>
              </q-item>
            </q-list>
          </q-card-section>
        </template>
        <template v-else>
          <q-card-section class="q-pt-none">
            <div class="q-mb-sm">
              Você vai cancelar {{ formatTotal(selecionadas.length, "venda", "vendas") }}. O cancelamento não pode ser desfeito; venda que já foi paga fica paga.
            </div>
            <q-banner v-if="hasSelecionadaComPix" dense class="bg-orange-1 text-orange-10 q-mb-sm" data-test="aviso-pix-pagavel-massa">
              O Pix já enviado das vendas com link ativo ainda pode ser pago até o corte. Se o cliente pagar, a venda volta como paga e entra na fila de conflito.
            </q-banner>
            <q-input
              v-model="motivoMassa"
              type="textarea"
              autogrow
              outlined
              dense
              label="Motivo"
              class="q-mb-sm"
              :maxlength="MOTIVO_MAX"
              data-test="motivo-cancelamento-massa"
            />
            <div class="q-mb-xs">
              Para confirmar, digite <span class="text-weight-bold" data-test="frase-esperada">{{ fraseMassa }}</span>
            </div>
            <q-input v-model="fraseDigitada" outlined dense autocomplete="off" data-test="frase-cancelamento" />
            <q-banner v-if="erroMassa" dense class="bg-red-1 text-negative q-mt-sm" data-test="erro-massa">{{ erroMassa }}</q-banner>
          </q-card-section>
          <q-card-actions align="right">
            <q-btn
              unelevated
              no-caps
              color="negative"
              :label="`Cancelar ${formatTotal(selecionadas.length, 'venda', 'vendas')}`"
              :loading="cancelandoMassa"
              :disable="!isMassaConfirmavel"
              data-test="confirmar-cancelamento-massa"
              @click="cancelMassa"
            />
          </q-card-actions>
        </template>
      </q-card>
    </q-dialog>

    <q-page-sticky v-if="isVendaOnlineConfiguravel" position="top-right" :offset="[0, 90]">
      <q-btn
        :color="drawerOpcoes ? 'red-7' : 'blue-7'"
        :icon="drawerOpcoes ? 'close' : 'settings'"
        class="rotate-0"
        style="border-top-left-radius: 50px;border-bottom-left-radius: 50px"
        data-test="opcoes-avancadas"
        @click="drawerOpcoes = !drawerOpcoes"
      >
        <q-tooltip
          :class="drawerOpcoes ? 'bg-negative text-white text-center' : 'bg-secondary text-white text-center'"
        >{{ drawerOpcoes ? "Fechar Opções Avançadas" : "Opções Avançadas" }}</q-tooltip>
      </q-btn>
    </q-page-sticky>

    <q-drawer
      v-if="isVendaOnlineConfiguravel"
      side="right"
      v-model="drawerOpcoes"
      bordered
      :width="300"
      :breakpoint="500"
      behavior="default"
      class="bg-white"
    >
      <q-scroll-area class="fit">
        <div class="q-pa-sm">
          <div class="q-table__title">Opções Avançadas</div>
          <div>
            <q-list class="rounded-borders" style="max-width: 350px; margin-left: -10px;width: 300px;">
              <q-item-label header></q-item-label>

              <q-item clickable v-ripple data-test="opcao-configuracoes" @click="openConfiguracoes">
                <q-item-section>
                  <q-item-label lines="1">Configurações da Venda Online</q-item-label>
                  <q-item-label caption>Validade do link, horários, estoque e planos de contas</q-item-label>
                </q-item-section>
              </q-item>
            </q-list>
          </div>
        </div>
      </q-scroll-area>
    </q-drawer>

    <dialog-resolver-conflito v-model="resolucaoAberta" :alvo="alvoResolucao" @resolvido="refreshDepoisDeResolver" />

    <vendas-online-modal-configuracao v-if="isVendaOnlineConfiguravel" v-model="modalConfiguracao" />
  </q-page>
</template>

<script>
import { mapGetters } from "vuex";
import { copyToClipboard } from "quasar";
import moment from "moment";
import DialogResolverConflito from "@/components/VendasOnline/dialog-resolver-conflito.vue";
import VendasOnlineIndicadores from "@/components/VendasOnline/vendas-online-indicadores.vue";
import VendasOnlineLista from "@/components/VendasOnline/vendas-online-lista.vue";
import VendasOnlineModalConfiguracao from "@/components/VendasOnline/vendas-online-modal-configuracao.vue";
import VendasOnlinePrevisao from "@/components/VendasOnline/vendas-online-previsao.vue";
import ContagemCancelamento from "@/components/VendasOnline/contagem-cancelamento.vue";
import SemPermissao from "@/components/Utils/sem-permissao.vue";
import {
  REGISTRO_CAUSAS,
  VENDA_ONLINE_CANCELAVEIS,
  formatConflitoMotivo,
  formatMomento,
  fraseCancelamento,
  getPendenciaAcao,
  getVendaOnlineStatus
} from "@/utils/venda-online";
import { periodoDeHoje, periodoParaApi } from "@/utils/venda-online-painel";
import { TEMPLATES_VENDA_ONLINE } from "@/utils/templates";

const REFRESH_MS = 15000;

const CONSULTA_INICIAL = { page: 1, limit: 25, sort: "created", direction: "desc" };

const EVENTOS = {
  gerada: "Venda gerada",
  registro_iniciado: "Registro do Pix iniciado",
  registrada: "Pix registrado, link ativo",
  registro_falhou: "Registro do Pix falhou",
  paga: "Paga",
  cancelada: "Cancelada",
  cancelamento_solicitado: "Cancelamento pedido; aguardando a confirmação",
  cancelamento_nao_confirmado: "O cancelamento não foi confirmado em 3 minutos; a conferência segue tentando",
  pago_durante_cancelamento: "Pago durante o cancelamento: a venda ficou paga",
  expirada: "Expirada",
  corte_falhou: "Corte falhou",
  valores_corrigidos: "Valores estimados trocados pelos informados no pagamento",
  alerta: "Alerta",
  reserva_devolvida: "Reserva devolvida ao estoque",
  estoque_baixado: "Estoque baixado",
  lancado_financeiro: "Lançado no financeiro",
  registrada_apos_cancelamento: "Pix registrado depois do cancelamento; a venda continua cancelada",
  conflito_resolvido: "Conflito resolvido",
  efeitos_descartados: "Baixa e lançamentos descartados: valor devolvido por fora",
  taxa_conveniencia_alterada: "Taxa de conveniência alterada"
};

const DECISOES_ROTULO = { honrar: "Honrar a venda", devolver: "Devolver o valor por fora" };

const MOTIVO_MAX = 255;

const RESULTADOS_MASSA = {
  cancelada: { rotulo: "Cancelado", cor: "grey-7" },
  cancelando: { rotulo: "Cancelando", cor: "orange-8" },
  ja_paga: { rotulo: "Já liquidado", cor: "positive" },
  erro: { rotulo: "Não cancelada", cor: "negative" },
  nao_processada: { rotulo: "Tente de novo", cor: "orange-8" }
};

const CANAIS_LINK = { whatsapp: "WhatsApp", email: "e-mail" };

const PAPEIS = {
  credito: "Crédito da venda",
  taxa: "Taxa de cobrança",
  tarifa: "Tarifa DouraSoft"
};

const LIQUIDACAO_ALERTAS = {
  valores_estimados: () => "taxa, líquido ou data do crédito estimados",
  pagamento_duplicado: (p) => `outro pagamento para esta venda (transação ${p.id_transaction})`,
  transacao_divergente: (p) => `paga pela transação ${p.id_transaction}, diferente da cobrança`,
  pagamento_nao_aplicado: () => "pagamento recebido antes da cobrança ser registrada",
  pagamento_nao_confirmado: () => "aviso de pagamento não confirmado",
  empresa_inativa: () => "pagamento recebido com a empresa inativa",
  aviso_orfao: () => "aviso de pagamento sem venda",
  status_pos_pago: (p) => `a cobrança informou ${p.status_nome || `o status ${p.status}`} depois do pagamento`,
  status_desconhecido: (p) => `status desconhecido da cobrança (${p.status_nome || p.status})`,
  referencia_divergente: (p) => `a transação ${p.id_transaction} é da cobrança ${p.recebida || "sem referência"}, não desta venda`
};

const INVALIDACOES = {
  nao_suportado: "Cobrança mantida até expirar no corte",
  ok: "Cobrança cancelada",
  falhou: "Cancelar a cobrança falhou"
};

const ORIGENS = { operadora: "Operador", webhook: "Pagamento", rotina: "Sistema", consulta: "Consulta" };

export default {
  name: "VendasOnline",
  components: { ContagemCancelamento, DialogResolverConflito, SemPermissao, VendasOnlineIndicadores, VendasOnlineLista, VendasOnlineModalConfiguracao, VendasOnlinePrevisao },
  data() {
    return {
      drawerOpcoes: false,
      modalConfiguracao: false,
      periodo: periodoDeHoje(),
      status: "",
      consulta: { ...CONSULTA_INICIAL },
      operadoraId: null,
      operadorasConhecidas: {},
      erro: null,
      detalhe: null,
      detalheAberto: false,
      reenviando: false,
      cancelamentoAberto: false,
      motivoCancelamento: "",
      cancelando: false,
      novaVenda: null,
      novaVendaDe: null,
      resolucaoAberta: false,
      alvoResolucao: null,
      cancelandoIds: [],
      selecionadas: [],
      massaAberta: false,
      motivoMassa: "",
      fraseDigitada: "",
      cancelandoMassa: false,
      erroMassa: null,
      resultadosMassa: null,
      resumoMassa: "",
      codigosSelecionados: {},
      MOTIVO_MAX,
      RESULTADOS_MASSA,
      timer: null
    };
  },
  computed: {
    ...mapGetters([
      "vendasOnlineLista",
      "vendasOnlineIndicadores",
      "isVendasOnlineListaLoading",
      "vendaOnlineNovaVenda",
      "hasVendaOnlineListarPermissao",
      "hasVendaOnlineCriarPermissao",
      "hasVendaOnlineEditarPermissao",
      "isVendaOnlineConfiguravel",
      "vendaOnlineConfiguracao",
      "isEquipeDourasoft"
    ]),
    pendencias() {
      if (!this.isVendaOnlineConfiguravel || !this.vendaOnlineConfiguracao) return [];

      return this.vendaOnlineConfiguracao.pendencias || [];
    },
    pendenciasDoAviso() {
      return this.pendencias.map((pendencia) => ({ ...pendencia, acao: getPendenciaAcao(pendencia, this.isEquipeDourasoft) }));
    },
    avisos() {
      if (!this.isVendaOnlineConfiguravel || !this.vendaOnlineConfiguracao) return [];

      return this.vendaOnlineConfiguracao.avisos || [];
    },
    whatsappUrl() {
      if (!this.detalhe || !this.detalhe.link_url) return null;

      let numero = String(this.detalhe.cliente_telefone || "").replace(/\D/g, "");
      if (numero.length === 10 || numero.length === 11) numero = "55" + numero;
      if (numero.length < 12) return null;

      return `https://wa.me/${numero}?text=${encodeURIComponent(this.detalhe.mensagem_whatsapp || this.detalhe.link_url)}`;
    },
    copiaGuardada() {
      const copia = this.vendaOnlineNovaVenda;

      return copia && this.detalhe && copia.venda_online_id === this.detalhe.id ? copia : null;
    },
    ofertaNovaVenda() {
      if (!this.detalhe) return null;
      if (this.novaVenda && this.novaVendaDe === this.detalhe.id) return this.novaVenda;

      return this.copiaGuardada;
    },
    operadoras() {
      return Object.entries(this.operadorasConhecidas).map(([value, label]) => ({ value: Number(value), label }));
    },
    fraseMassa() {
      return fraseCancelamento(this.selecionadas.length);
    },
    hasSelecionadaComPix() {
      return this.vendasOnlineLista.vendas.some((venda) => venda.status === "ativa" && this.selecionadas.includes(venda.id));
    },
    isReenviavel() {
      return Boolean(this.detalhe) && this.detalhe.status === "ativa" && this.hasVendaOnlineCriarPermissao;
    },
    isCancelavelNoDetalhe() {
      return Boolean(this.detalhe) && this.isSelectable(this.detalhe);
    },
    isMassaConfirmavel() {
      return this.selecionadas.length > 0 && this.motivoMassa.trim() !== "" && this.fraseDigitada.trim() === this.fraseMassa;
    }
  },
  watch: {
    status: "applyFiltros",
    periodo: "applyFiltros",
    operadoraId: "applyFiltros",
    isVendaOnlineConfiguravel: {
      immediate: true,
      handler(pode) {
        if (pode) this.$store.dispatch("fetchVendaOnlineConfiguracao");
      }
    },
    hasVendaOnlineListarPermissao: {
      immediate: true,
      handler(pode) {
        this.stopAtualizacao();
        if (!pode) return;

        this.reload();
        this.timer = setInterval(this.reload, REFRESH_MS);
      }
    }
  },
  beforeUnmount() {
    this.stopAtualizacao();
  },
  methods: {
    copyText(texto, mensagem) {
      return copyToClipboard(texto)
        .then(() => this.$q.notify({ message: mensagem, color: "green", position: "top-right" }))
        .catch(() => this.$q.notify({ message: "Não foi possível copiar.", color: "negative", position: "top-right" }));
    },
    openConfiguracoes() {
      this.drawerOpcoes = false;
      this.modalConfiguracao = true;
    },
    openAtalho(atalho) {
      if (atalho === "opcoes") {
        this.openConfiguracoes();
        return;
      }

      if (atalho === "templates") {
        this.$router.push({ path: "/parametros", query: { aba: "templates" } });
        return;
      }

      this.$router.push("/parametros");
    },
    formatConflitoMotivo,
    formatMomento,
    getVendaOnlineStatus,
    formatEventoRotulo({ tipo, payload }) {
      const motivo = payload?.motivo;
      if (tipo === "taxa_conveniencia_alterada" && payload) {
        return `Taxa de conveniência alterada de R$ ${this.$currency(payload.calculada)} para R$ ${this.$currency(payload.aplicada)}`;
      }
      if (tipo === "cancelada" && motivo) {
        return `Cancelada: ${motivo}`;
      }
      if (tipo === "cancelamento_solicitado" && motivo) {
        return `Cancelamento pedido: ${motivo}. Aguardando a confirmação`;
      }
      if (tipo === "registro_falhou" && payload?.tentativa) {
        const detalhe = REGISTRO_CAUSAS[motivo];
        return `Registro do Pix falhou (tentativa ${payload.tentativa}${detalhe ? `: ${detalhe}` : ""})`;
      }
      if (tipo === "expirada" && motivo === "sem_margem_para_registrar") {
        return "Expirada sem cobrança: o corte chegou antes do registro";
      }
      if (tipo === "invalidacao") {
        return INVALIDACOES[payload?.resultado] || INVALIDACOES.falhou;
      }
      if (tipo === "alerta" && motivo === "corte_atrasado") {
        return "Alerta: link ativo depois do corte";
      }
      if (tipo === "alerta" && motivo === "registro_parado") {
        const causa = REGISTRO_CAUSAS[payload.causa] || payload.causa;
        return `Alerta: registro do Pix parado depois de ${payload.tentativas} ${payload.tentativas === 1 ? "tentativa" : "tentativas"} (${causa})`;
      }
      if (tipo === "alerta" && motivo === "corte_falhou") {
        return `Alerta: o corte falhou ${payload.falhas} vezes`;
      }
      if (tipo === "alerta" && motivo === "valor_divergente") {
        return `Alerta: pago R$ ${this.$currency(payload.pago)}, esperado R$ ${this.$currency(payload.esperado)}`;
      }
      if (tipo === "alerta" && motivo === "valores_incoerentes" && payload.taxa_informada !== undefined) {
        return `Alerta: o pagamento informou taxa R$ ${this.$currency(payload.taxa_informada)} para o pago R$ ${this.$currency(payload.pago)}; taxa não usada`;
      }
      if (tipo === "alerta" && motivo === "pagamento_divergente") {
        return `Alerta: pagamento da transação ${payload.id_transaction} não confere (pago R$ ${this.$currency(payload.pago)}, esperado R$ ${this.$currency(payload.esperado)}); venda expirada em conflito`;
      }
      if (tipo === "alerta" && motivo === "valores_incoerentes") {
        return `Alerta: o pagamento informou líquido R$ ${this.$currency(payload.liquido_informado)}; gravado R$ ${this.$currency(payload.liquido)} (pago menos taxa)`;
      }
      if (tipo === "alerta" && motivo === "taxa_acima_do_pago") {
        return `Alerta: custo estimado R$ ${this.$currency(payload.taxa_estimada)} acima do pago R$ ${this.$currency(payload.pago)}; taxa limitada ao pago`;
      }
      if (tipo === "alerta" && LIQUIDACAO_ALERTAS[motivo]) {
        return `Alerta: ${LIQUIDACAO_ALERTAS[motivo](payload)}`;
      }
      if (tipo === "alerta" && motivo === "lancamento_falhou" && payload.etapa === "correcao") {
        return `Alerta: a correção dos valores no financeiro ficou pendente (${payload.mensagem})`;
      }
      if (tipo === "alerta" && motivo === "lancamento_falhou") {
        return `Alerta: o financeiro não recebeu os lançamentos desta venda (${payload.mensagem})`;
      }
      if (tipo === "alerta" && motivo === "configuracao_incompleta") {
        const pendencias = (payload.pendencias || []).map((pendencia) => pendencia.mensagem).join(" ");
        return `Alerta: os lançamentos no financeiro aguardam a configuração (${pendencias})`;
      }
      if (tipo === "alerta" && motivo === "lancamento_cancelado_manual") {
        return `Alerta: ${PAPEIS[payload.papel] || payload.papel} cancelado no financeiro; a correção dos valores não mexeu nele`;
      }
      if (tipo === "alerta" && motivo === "estoque_insuficiente") {
        return `Alerta: ${payload.produto} ficou com saldo ${payload.saldo} no estoque do operador (${payload.quantidade} vendidos)`;
      }
      if (tipo === "estoque_baixado" && payload?.negativos) {
        const itens = payload.negativos === 1 ? "1 item ficou" : `${payload.negativos} itens ficaram`;
        return `Estoque baixado: ${itens} com saldo negativo`;
      }
      if (tipo === "link_enfileirado" || tipo === "link_reenviado") {
        const canal = CANAIS_LINK[payload?.canal] || payload?.canal;
        return `${tipo === "link_enfileirado" ? "Link na fila de envio" : "Reenvio do link na fila"} por ${canal}`;
      }
      if (tipo === "alerta" && motivo === "envio_link_falhou") {
        return "Alerta: o envio do link ao cliente ficou pendente";
      }
      if (tipo === "alerta" && motivo === "whatsapp_sem_modelo") {
        return payload?.causa === "sem_template"
          ? `Link não enviado por WhatsApp: falta o template ${TEMPLATES_VENDA_ONLINE["vendaonline-whatsapp"]}`
          : "Link não enviado por WhatsApp: a empresa só envia mensagens por modelo aprovado";
      }
      if (tipo === "alerta" && motivo === "email_sem_modelo") {
        return `Link não enviado por e-mail: falta o template ${TEMPLATES_VENDA_ONLINE.vendaonline}`;
      }
      if (tipo === "lembrete_enfileirado") {
        return `Lembrete na fila de envio por ${CANAIS_LINK[payload?.canal] || payload?.canal}`;
      }
      if (tipo === "transferida") {
        const caixa = (lado) => `${lado?.caixa || "sem nome"}${lado?.operadora ? ` (${lado.operadora})` : ""}`;
        return `Transferida do caixa ${caixa(payload?.de)} para o caixa ${caixa(payload?.para)}; a comissão continua com a operadora da venda`;
      }
      if (tipo === "conflito_resolvido") {
        const decisao = DECISOES_ROTULO[payload?.decisao];
        const nota = payload?.nota ? ` — ${payload.nota}` : "";
        return `Conflito resolvido${decisao ? `: ${decisao}` : ""}${nota}`;
      }
      if (tipo === "aviso") {
        const status = payload?.status_nome || `status ${payload?.status}`;
        return payload?.autenticado === false ? `Aviso de pagamento: ${status}, aguardando confirmação` : `Aviso de pagamento: ${status}`;
      }
      return EVENTOS[tipo] || tipo;
    },
    formatEventoOrigem(origem) {
      return ORIGENS[origem] || origem;
    },
    filtrosDosIndicadores() {
      const filtros = periodoParaApi(this.periodo);
      if (this.status) filtros.status = this.status;
      if (this.operadoraId) filtros.operadora_id = this.operadoraId;

      return filtros;
    },
    async loadLista() {
      const resultado = await this.$store.dispatch("fetchVendasOnlineLista", { ...this.filtrosDosIndicadores(), ...this.consulta });
      if (resultado.antiga) return;
      this.erro = resultado.ok ? null : resultado.mensagem;
      this.rememberOperadoras();
      if (resultado.ok && !this.massaAberta) this.keepSelecionaveis();
      if (resultado.ok) this.followCancelando();
    },
    followCancelando() {
      const vendas = this.vendasOnlineLista.vendas;
      vendas
        .filter((venda) => venda.status === "paga" && this.cancelandoIds.includes(venda.id))
        .forEach((venda) => {
          this.$q.notify({
            message: `${venda.codigo} foi paga durante o cancelamento: a venda ficou paga e a cota fica com o cliente.`,
            color: "orange",
            position: "top-right",
            timeout: 0,
            actions: [{ icon: "close", color: "white" }]
          });
        });
      const vistas = vendas.map((venda) => venda.id);
      this.cancelandoIds = [
        ...this.cancelandoIds.filter((id) => !vistas.includes(id)),
        ...vendas.filter((venda) => venda.status === "cancelando").map((venda) => venda.id)
      ];
      if (this.detalheAberto && this.detalhe?.status === "cancelando" && !this.cancelamentoAberto) this.open(this.detalhe);
    },
    followIds(ids) {
      this.cancelandoIds = [...this.cancelandoIds.filter((id) => !ids.includes(id)), ...ids];
    },
    load() {
      return Promise.all([this.loadLista(), this.$store.dispatch("fetchVendasOnlineIndicadores", this.filtrosDosIndicadores())]);
    },
    applyFiltros() {
      this.consulta = { ...this.consulta, page: 1 };
      this.load();
    },
    requestPagina(consulta) {
      this.consulta = consulta;
      this.loadLista();
    },
    isSelectable(venda) {
      return this.hasVendaOnlineCriarPermissao && VENDA_ONLINE_CANCELAVEIS.includes(venda.status);
    },
    keepSelecionaveis() {
      const abertas = this.vendasOnlineLista.vendas.filter((venda) => this.isSelectable(venda)).map((venda) => venda.id);
      this.selecionadas = this.selecionadas.filter((id) => abertas.includes(id));
    },
    openCancelamento() {
      this.motivoCancelamento = "";
      this.cancelamentoAberto = true;
    },
    async cancelVenda() {
      const motivo = this.motivoCancelamento.trim();
      if (!motivo || this.cancelando) return;

      this.cancelando = true;
      let resultado;
      try {
        resultado = await this.$store.dispatch("cancelVendaOnline", { id: this.detalhe.id, motivo });
      } finally {
        this.cancelando = false;
      }
      this.cancelamentoAberto = false;

      if (!resultado.ok) {
        this.$q.notify({ message: resultado.mensagem, color: "negative", position: "top-right" });
        await this.open(this.detalhe);
        this.load();
        return;
      }

      this.detalhe = resultado.venda;
      const cancelada = resultado.venda.status === "cancelada";
      if (resultado.venda.status === "cancelando") this.followIds([resultado.venda.id]);
      this.novaVenda = cancelada ? resultado.novaVenda || null : null;
      this.novaVendaDe = cancelada ? resultado.venda.id : null;
      this.$q.notify({ message: resultado.mensagem, color: cancelada ? "green" : "orange", position: "top-right" });
      this.load();
    },
    startNovaVenda() {
      const novaVenda = this.ofertaNovaVenda;
      if (!novaVenda.movcaixa_id) {
        this.$q.notify({
          message: "Você não tem caixa aberto. Abra o seu caixa e clique em Fazer nova venda de novo.",
          color: "orange",
          position: "top-right"
        });
        return;
      }

      this.novaVenda = null;
      this.novaVendaDe = null;
      this.$store.commit("setVendaOnlineNovaVenda", { ...novaVenda, venda_online_id: this.detalhe.id });
      this.detalheAberto = false;
      this.$router.push(`/caixas/${novaVenda.movcaixa_id}`);
    },
    openCancelamentoMassa() {
      this.motivoMassa = "";
      this.fraseDigitada = "";
      this.erroMassa = null;
      this.resultadosMassa = null;
      this.massaAberta = true;
    },
    async cancelMassa() {
      if (!this.isMassaConfirmavel || this.cancelandoMassa) return;

      this.cancelandoMassa = true;
      this.erroMassa = null;
      this.codigosSelecionados = Object.fromEntries(
        this.vendasOnlineLista.vendas.filter((venda) => this.selecionadas.includes(venda.id)).map((venda) => [venda.id, venda.codigo])
      );
      let resultado;
      try {
        resultado = await this.$store.dispatch("cancelVendasOnline", {
          ids: [...this.selecionadas],
          motivo: this.motivoMassa.trim(),
          frase: this.fraseDigitada.trim()
        });
      } finally {
        this.cancelandoMassa = false;
      }

      if (!resultado.ok) {
        this.erroMassa = resultado.mensagem;
        return;
      }

      this.resultadosMassa = resultado.resultados;
      this.followIds(resultado.resultados.filter((item) => item.resultado === "cancelando").map((item) => item.id));
      this.resumoMassa = resultado.mensagem;
      this.selecionadas = [];
      this.load();
    },
    formatPapel(papel) {
      return PAPEIS[papel] || papel;
    },
    formatData(data) {
      return data ? moment(data, "YYYY-MM-DD").format("DD/MM/YYYY") : "";
    },
    formatTotal(total, singular, plural) {
      return `${total} ${total === 1 ? singular : plural}`;
    },
    stopAtualizacao() {
      clearInterval(this.timer);
      this.timer = null;
    },
    reload() {
      return this.load();
    },
    rememberOperadoras() {
      const vendas = this.vendasOnlineLista.vendas;
      const conhecidas = { ...this.operadorasConhecidas };
      vendas.forEach((venda) => {
        conhecidas[venda.operadora_cadastro_id] = venda.operadora_nome;
      });
      this.operadorasConhecidas = conhecidas;
    },
    openResolucao(alvo) {
      this.alvoResolucao = alvo;
      this.resolucaoAberta = true;
    },
    async refreshDepoisDeResolver() {
      await this.load();
      if (this.detalheAberto && this.detalhe) {
        const resultado = await this.$store.dispatch("fetchVendaOnline", this.detalhe.id);
        if (resultado.ok) this.detalhe = resultado.venda;
      }
    },
    async copyLinkDaLinha(venda) {
      const resultado = await this.$store.dispatch("fetchVendaOnline", venda.id);
      if (!resultado.ok) {
        this.$q.notify({ message: resultado.mensagem, color: "negative", position: "top-right" });
        return;
      }
      if (!resultado.venda.link_url) {
        this.$q.notify({ message: "Esta venda não tem link para copiar.", color: "orange", position: "top-right" });
        return;
      }

      await this.copyText(resultado.venda.link_url, "Link copiado");
    },
    async resendDaLinha(venda) {
      const resultado = await this.$store.dispatch("resendVendaOnlineLink", venda.id);
      this.$q.notify({ message: resultado.mensagem, color: resultado.ok ? "green" : "negative", position: "top-right" });
    },
    async cancelDaLinha(venda) {
      await this.open(venda);
      if (this.detalheAberto && this.detalhe?.id === venda.id) this.openCancelamento();
    },
    async open(venda) {
      const resultado = await this.$store.dispatch("fetchVendaOnline", venda.id);
      if (!resultado.ok) {
        this.$q.notify({ message: resultado.mensagem, color: "negative", position: "top-right" });
        return;
      }

      if (this.detalhe?.id !== resultado.venda.id) this.novaVenda = null;
      this.detalhe = resultado.venda;
      this.detalheAberto = true;
    },
    async resendLink() {
      this.reenviando = true;
      let resultado;
      try {
        resultado = await this.$store.dispatch("resendVendaOnlineLink", this.detalhe.id);
      } finally {
        this.reenviando = false;
      }

      if (!resultado.ok) {
        this.$q.notify({ message: resultado.mensagem, color: "negative", position: "top-right" });
        return;
      }

      this.$q.notify({ message: resultado.mensagem, color: "green", position: "top-right" });
      await this.open(this.detalhe);
    }
  }
};
</script>
